import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  BarChart3,
  CreditCard,
  FileText,
  Folder,
  KeyRound,
  LayoutDashboard,
  Map,
  MessageSquare,
  Settings,
  SwatchBook,
  Users,
} from "lucide-react";

export interface AdminNavItem {
  label: string;
  description: string;
  path: string;
  icon: LucideIcon;
  group: "Overview" | "Moderation" | "Management" | "Tools";
}

export interface AdminPageMeta {
  title: string;
  description: string;
  section: string;
}

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    label: "Dashboard",
    description: "Overview",
    path: "/admin",
    icon: LayoutDashboard,
    group: "Overview",
  },
  {
    label: "Analytics",
    description: "Traffic",
    path: "/admin/analytics",
    icon: BarChart3,
    group: "Overview",
  },
  {
    label: "Review Listings",
    description: "Listings",
    path: "/admin/review-listings",
    icon: FileText,
    group: "Moderation",
  },
  {
    label: "Reported Reviews",
    description: "Reports",
    path: "/admin/reported-reviews",
    icon: AlertCircle,
    group: "Moderation",
  },
  {
    label: "Review Moderation",
    description: "All reviews",
    path: "/admin/reviews",
    icon: MessageSquare,
    group: "Moderation",
  },
  {
    label: "Users",
    description: "Accounts",
    path: "/admin/users",
    icon: Users,
    group: "Management",
  },
  {
    label: "Subscriptions",
    description: "Plans",
    path: "/admin/subscriptions",
    icon: CreditCard,
    group: "Management",
  },
  {
    label: "Categories",
    description: "Types",
    path: "/admin/categories",
    icon: Folder,
    group: "Management",
  },
  {
    label: "Areas",
    description: "Coverage",
    path: "/admin/areas",
    icon: Map,
    group: "Management",
  },
  {
    label: "Application Settings",
    description: "Config",
    path: "/admin/settings",
    icon: Settings,
    group: "Tools",
  },
  {
    label: "Support API Key",
    description: "Machine access",
    path: "/admin/support-api",
    icon: KeyRound,
    group: "Tools",
  },
  {
    label: "Icon Browser",
    description: "Icons",
    path: "/admin/icons",
    icon: SwatchBook,
    group: "Tools",
  },
];

const ADMIN_PAGE_META: Array<{
  path: string;
  meta: AdminPageMeta;
}> = [
  {
    path: "/admin/support-api",
    meta: {
      title: "Support API Key",
      description: "Issue and rotate the support machine key.",
      section: "Tools",
    },
  },
  {
    path: "/admin/reported-reviews",
    meta: {
      title: "Reported Reviews",
      description: "Review reports and moderate flagged content.",
      section: "Moderation",
    },
  },
  {
    path: "/admin/review-listings",
    meta: {
      title: "Review Listings",
      description: "Work through the listing review queue.",
      section: "Moderation",
    },
  },
  {
    path: "/admin/create-listing",
    meta: {
      title: "Create Listing",
      description: "Create a listing from the admin side.",
      section: "Management",
    },
  },
  {
    path: "/admin/edit-listing",
    meta: {
      title: "Edit Listing",
      description: "Edit listing details and moderation status.",
      section: "Moderation",
    },
  },
  {
    path: "/admin/subscriptions",
    meta: {
      title: "Subscriptions",
      description: "Manage plans and verification status.",
      section: "Management",
    },
  },
  {
    path: "/admin/settings",
    meta: {
      title: "Application Settings",
      description: "Manage settings, templates, and feature flags.",
      section: "Tools",
    },
  },
  {
    path: "/admin/categories",
    meta: {
      title: "Categories",
      description: "Manage category labels and icons.",
      section: "Management",
    },
  },
  {
    path: "/admin/areas",
    meta: {
      title: "Areas",
      description: "Manage service areas and territories.",
      section: "Management",
    },
  },
  {
    path: "/admin/analytics",
    meta: {
      title: "Analytics",
      description: "Track traffic, searches, and engagement.",
      section: "Overview",
    },
  },
  {
    path: "/admin/users",
    meta: {
      title: "Users",
      description: "Manage account access and account status.",
      section: "Management",
    },
  },
  {
    path: "/admin/reviews",
    meta: {
      title: "Review Moderation",
      description: "Moderate the full review queue.",
      section: "Moderation",
    },
  },
  {
    path: "/admin/icons",
    meta: {
      title: "Icon Browser",
      description: "Browse the icon set used in listings.",
      section: "Tools",
    },
  },
  {
    path: "/admin",
    meta: {
      title: "Dashboard",
      description: "Pending work, recent activity, and platform totals.",
      section: "Overview",
    },
  },
];

export const isAdminPathActive = (pathname: string, path: string) => {
  if (path === "/admin") {
    return pathname === path;
  }

  return pathname === path || pathname.startsWith(`${path}/`);
};

export const getAdminPageMeta = (pathname: string): AdminPageMeta => {
  const match = ADMIN_PAGE_META.find((entry) => isAdminPathActive(pathname, entry.path));
  return (
    match?.meta ?? {
      title: "Admin",
      description: "Manage the operational side of the product from a single workspace.",
      section: "Administration",
    }
  );
};
